import type { MouseEvent } from 'react';
import '../../../shared-web/site-chrome.css';

/**
 * The page-level Back row for the marketing-bundle pages — the same contract
 * as the app's shared PageBack (docs/frontend-ui-standard.md §3, "Page-level
 * Back and title contract"), reproduced here because these pages are separate
 * documents with no SPA router to pop.
 *
 * Back returns: a left-click pops the browser trip whenever the entry behind
 * this one is inside Ember (same-origin referrer), so the history stack
 * shrinks and the browser's own Back keeps meaning the same thing. A cold
 * arrival (a shared link, a search result) replaces the current entry with the
 * page's safe destination instead of pushing a new one. The href stays a real
 * path so middle-click, cmd-click, and copy-link behave like a link.
 *
 * The row also carries the shared Home control on its right edge, on every
 * marketing page (2026-08-27 拍板). These pages sit outside the app's route
 * ladder, so their Back only retraces one hop at a time — footer → narrative
 * page → legal page piles up a trail with no one-tap way home, and a cold
 * arrival has no trail at all. Home is that exit, everywhere in this bundle.
 */

/**
 * Read inside the Ember app's in-app browser (`?embed=1`).
 *
 * The app opens these pages in its own full-screen page, which already carries
 * a back arrow and the page's title (iOS WebPageView, 2026-09-14). This row
 * would be a second navigation stacked under the first, and its Home would
 * walk the reader out of the app into the marketing site with no way back.
 * So in embed mode the row stands down and lets the app's chrome be the only
 * one. Guarded for the SSR prerender, which has no window at all.
 *
 * That guard is exactly why this check alone is not enough: these pages are
 * prerendered at build time, so the row ships inside the HTML and this function
 * only gets to remove it at hydration — the reader sees a navigation row appear
 * and then vanish. The other half lives in `index.html`: a pre-paint script
 * stamps `html.eh-embed` and one inline rule hides `.eh-page-back-row`, so the
 * row never gets a frame. Keep the two in step — if this component's root class
 * changes, that rule changes with it.
 */
export function embeddedInApp(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return false;
  }
}

/**
 * Same-origin referrer path, or '' — the only history signal a fresh document
 * has. Guarded for the SSR prerender, which has no document at all.
 */
function entryReferrerPath(): string {
  if (typeof document === 'undefined') return '';
  try {
    if (!document.referrer) return '';
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return '';
    if (ref.pathname === window.location.pathname) return '';
    return ref.pathname;
  } catch {
    return '';
  }
}

/**
 * Destination name for the accessible label — same vocabulary as the app
 * router's returnToLabel and the /collect page's copy of it.
 */
function labelFor(path: string, fallback: string): string {
  if (!path) return fallback;
  if (path === '/') return 'Home';
  if (path === '/me' || path === '/me/embers' || path === '/me/requests') return 'Profile';
  return fallback;
}

export default function PageBack({
  /** The page's documented safe destination for a cold arrival. */
  fallback = '/',
  /** Chrome theme: pin light/dark for pages whose own ground is fixed. */
  theme = 'light',
}: { fallback?: string; theme?: 'auto' | 'light' | 'dark' } = {}) {
  // 在 App 的站内浏览器里读:让 App 自己的顶栏做唯一的导航
  if (embeddedInApp()) return null;
  const label = labelFor(entryReferrerPath(), 'Home');
  const chrome = `eh-chrome${theme === 'light' ? ' eh-chrome-light' : ''}${theme === 'dark' ? ' eh-chrome-dark' : ''}`;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (entryReferrerPath()) {
      window.history.back();
      return;
    }
    window.location.replace(fallback);
  };

  return (
    <div className="eh-page-back-row-inner">
      <a
        href={fallback}
        className={`${chrome} eh-page-back`}
        aria-label={`Back to ${label}`}
        data-log="page_back"
        onClick={handleClick}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m14.5 5-7 7 7 7" />
        </svg>
      </a>
      <a
        href="/"
        className={`${chrome} eh-page-home`}
        aria-label="Home"
        data-log="page_home"
      >
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m4 11.2 8-7.2 8 7.2" />
          <path d="M6.2 9.9v8.9a1.7 1.7 0 0 0 1.7 1.7h8.2a1.7 1.7 0 0 0 1.7-1.7V9.9" />
        </svg>
        <span>Home</span>
      </a>
    </div>
  );
}
